/**
 * Global quarterly pricing amounts and geolocation currency mapping.
 * Plans: starter (40–100), growth (101–200). AED is the canonical price source.
 */

export const PRICING_AMOUNTS = {
  AED: { starter: 699, growth: 1299 },
  SAR: { starter: 699, growth: 1299 },
  QAR: { starter: 699, growth: 1299 },
  USD: { starter: 189, growth: 349 },
  EUR: { starter: 169, growth: 319 },
  INR: { starter: 15599, growth: 28999 },
  OMR: { starter: 79, growth: 139 },
  SGD: { starter: 249, growth: 459 },
  AUD: { starter: 279, growth: 519 },
  MYR: { starter: 849, growth: 1599 },
  JPY: { starter: 27999, growth: 51999 },
  CNY: { starter: 1299, growth: 2499 },
  IDR: { starter: 2959000, growth: 5499000 },
};

/** ISO country code → currency code */
export const COUNTRY_TO_CURRENCY = {
  IN: 'INR',
  US: 'USD',
  AE: 'AED',
  SA: 'SAR',
  QA: 'QAR',
  OM: 'OMR',
  BH: 'AED',
  KW: 'AED',
  SG: 'SGD',
  AU: 'AUD',
  NZ: 'AUD',
  MY: 'MYR',
  JP: 'JPY',
  CN: 'CNY',
  ID: 'IDR',
  HK: 'USD',
  PH: 'USD',
  TH: 'USD',
  VN: 'USD',
  KR: 'USD',
  TW: 'USD',
};

/** European countries billed in EUR */
export const EUR_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT',
  'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'GB', 'CH', 'NO',
  'IS', 'LI', 'AD', 'MC', 'SM', 'VA', 'ME', 'MK', 'AL', 'RS', 'BA', 'UA', 'MD', 'BY', 'XK',
]);

export const CURRENCY_GROUPS = [
  {
    label: 'Global',
    codes: ['USD', 'EUR', 'INR'],
  },
  {
    label: 'Middle East (Gulf)',
    codes: ['AED', 'SAR', 'QAR', 'OMR'],
  },
  {
    label: 'Asia-Pacific & Oceania',
    codes: ['SGD', 'AUD', 'MYR', 'JPY', 'CNY', 'IDR'],
  },
];

export const CURRENCY_LABELS = {
  USD: 'USD ($) — United States / Global',
  EUR: 'EUR (€) — Europe',
  INR: 'INR (₹) — India',
  AED: 'AED — UAE',
  SAR: 'SAR — Saudi Arabia',
  QAR: 'QAR — Qatar',
  OMR: 'OMR — Oman',
  SGD: 'SGD ($) — Singapore',
  AUD: 'AUD ($) — Australia / Oceania',
  MYR: 'MYR — Malaysia',
  JPY: 'JPY (¥) — Japan',
  CNY: 'CNY (¥) — China',
  IDR: 'IDR (Rp) — Indonesia',
};

/** Format a numeric amount with locale-aware commas and currency symbol */
export function formatPrice(amount, currencyCode) {
  const n = Number(amount);
  if (Number.isNaN(n)) return '';

  const formatted = n.toLocaleString('en-US', { maximumFractionDigits: 0 });

  switch (currencyCode) {
    case 'INR':
      return `₹${formatted}`;
    case 'USD':
      return `$${formatted}`;
    case 'EUR':
      return `€${formatted}`;
    case 'SGD':
      return `S$${formatted}`;
    case 'AUD':
      return `A$${formatted}`;
    case 'JPY':
    case 'CNY':
      return `¥${formatted}`;
    case 'IDR':
      return `Rp ${formatted}`;
    case 'AED':
      return `AED ${formatted}`;
    case 'SAR':
      return `SAR ${formatted}`;
    case 'QAR':
      return `QAR ${formatted}`;
    case 'OMR':
      return `OMR ${formatted}`;
    case 'MYR':
      return `RM ${formatted}`;
    default:
      return `$${formatted}`;
  }
}

export function getCurrencySymbol(currencyCode) {
  const map = {
    USD: '$',
    INR: '₹',
    EUR: '€',
    AED: 'AED',
    SAR: 'SAR',
    QAR: 'QAR',
    OMR: 'OMR',
    SGD: 'S$',
    AUD: 'A$',
    MYR: 'RM',
    JPY: '¥',
    CNY: '¥',
    IDR: 'Rp',
  };
  return map[currencyCode] ?? '$';
}

export function currencyFromCountry(countryCode) {
  if (!countryCode) return 'USD';
  const cc = countryCode.toUpperCase();
  if (COUNTRY_TO_CURRENCY[cc]) return COUNTRY_TO_CURRENCY[cc];
  if (EUR_COUNTRIES.has(cc)) return 'EUR';
  return 'USD';
}

export function getPlanPriceDisplay(planId, currencyCode) {
  const amounts = PRICING_AMOUNTS[currencyCode] ?? PRICING_AMOUNTS.USD;
  const amount = amounts[planId];
  if (amount == null) {
    const fallback = PRICING_AMOUNTS.USD[planId];
    if (fallback == null) return formatPrice(0, currencyCode);
    return formatPrice(fallback, 'USD');
  }
  return formatPrice(amount, currencyCode);
}

export async function detectCurrencyFromGeo() {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    const res = await fetch('https://ipapi.co/json/', { signal: controller.signal });
    clearTimeout(timeout);
    if (!res.ok) throw new Error('geo unavailable');
    const data = await res.json();
    return currencyFromCountry(data.country_code);
  } catch {
    return 'USD';
  }
}
